// src/pages/PrivacyPage.js
import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Database, Clock, Mail, Lock, Calendar, AlertTriangle, Users, Globe, CreditCard, FileWarning, UserX } from 'lucide-react';
import { pageVariants, pageTransition } from '../utils/pageTransitions';

const PrivacyPage = () => {
  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
      className="min-h-screen pt-20"
    >
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-center mb-16"
            >
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Privacy Notice for HousingAI (The Company)
              </h1>
              <p className="text-xl text-gray-600">
                Last updated: September 05, 2025
              </p>
            </motion.div>

            {/* Introduction */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <p className="text-gray-700 leading-relaxed">
                The "Service" provided by HousingAI is a multilingual, AI-powered chat assistant available 24/7 to help the general public, 
                subsidized housing applicants, and subsidized housing tenants easily find answers to frequently asked questions about 
                subsidized housing programs and services.
              </p>
            </motion.div>

            {/* Section 1 */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Database className="h-8 w-8 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900">1. Collecting and Using Your Personal Data</h2>
              </div>
              <p className="text-gray-700 mb-4">
                <strong>Information you provide:</strong> We may collect and store any personal or sensitive information you enter into the Chatbot.
              </p>
              <p className="text-gray-700 mb-4">
                We may collect voluntarily provided personal information, including sensitive data, and other information. 
                "Personal Information" means individually identifiable information that would allow us to determine the actual identity of, 
                and contact, a specific living person. "Sensitive Data" means information, comments, or content that you optionally provide 
                that may reveal your ethnic origin, nationality, or religion.
              </p>
              <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                <p className="text-gray-700">
                  By voluntarily providing Sensitive Data to us, you consent to the collection, use, and disclosure of Sensitive Data as 
                  permitted by applicable privacy law. When you provide personal information, it may be sent to servers located in the 
                  United States and other countries worldwide.
                </p>
              </div>
            </motion.div>

            {/* Section 2 */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Shield className="h-8 w-8 text-green-600" />
                <h2 className="text-2xl font-bold text-gray-900">2. Data You Don't Need to Provide</h2>
              </div>
              <p className="text-gray-700 mb-4">
                This chat widget connects you to an AI-powered assistant (ChatGPT), not a real person. It provides instant, 
                automated responses to help answer your questions quickly.
              </p>
              <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded-r-xl">
                <p className="text-gray-800">
                  <strong>You can use the chat and ask questions without providing any personal details</strong>, such as your name, 
                  email, address, or phone number. We do not require or request such information for you to use the HousingAI chat 
                  Widget Service. There is no need to create an account or provide identity information to use the widget.
                </p>
              </div>
            </motion.div>

            {/* Section 3 - OpenAI Warning */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <AlertTriangle className="h-8 w-8 text-orange-600" />
                <h2 className="text-2xl font-bold text-gray-900">3. Your Rights & Choices</h2>
              </div>
              
              {/* OpenAI Warning Box */}
              <div className="bg-orange-50 border-2 border-orange-400 rounded-xl p-6 mb-6">
                <h3 className="text-xl font-bold text-orange-800 mb-3 flex items-center gap-2">
                  <FileWarning className="h-6 w-6" />
                  IMPORTANT WARNING ABOUT OPENAI DATA HANDLING
                </h3>
                <p className="text-gray-700 mb-3">
                  When you use our chat widget, your messages are sent directly to OpenAI for processing. 
                  OpenAI retains all data sent through their API for 30 days for their monitoring and safety purposes. This means:
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                    <span className="text-gray-700">Your messages will be stored on OpenAI's servers for 30 days</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                    <span className="text-gray-700">OpenAI may review this data during that period</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                    <span className="text-gray-700">After 30 days, OpenAI states they delete the data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-orange-600 rounded-full mt-2"></div>
                    <span className="text-gray-700">We have no control over this retention or how OpenAI handles your data</span>
                  </li>
                </ul>
                <p className="text-gray-700 mt-4">
                  You should review OpenAI's current data usage policies at:{' '}
                  <a href="https://openai.com/policies/api-data-usage-policies" className="text-blue-600 hover:underline">
                    https://openai.com/policies/api-data-usage-policies
                  </a>
                </p>
                <p className="text-orange-800 font-bold mt-3">
                  By using this service, you acknowledge that you understand your data will be subject to OpenAI's policies 
                  and 30-day retention period.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Data Deletion</h3>
                  <p className="text-gray-700">
                    You may request deletion of your prompt data from our server logs. Contact us at{' '}
                    <a href="mailto:privacy@gethousingai.com" className="text-blue-600 hover:underline">privacy@gethousingai.com</a>{' '}
                    with the details of the prompt(s) you would like to delete. If we can locate the relevant log entries, 
                    we will delete them within five business days.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Contacting OpenAI</h3>
                  <p className="text-gray-700">
                    For questions about how OpenAI may use, store, or delete data that was processed by their API, 
                    you should contact OpenAI directly via their support channels.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Section 4 - Usage Data */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Database className="h-8 w-8 text-purple-600" />
                <h2 className="text-2xl font-bold text-gray-900">4. Usage Data</h2>
              </div>
              <p className="text-gray-700 mb-4">
                When you use our Services, we automatically collect certain information called usage data, including:
              </p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Your device's internet protocol (IP) address</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Browser type and version</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Pages visited and time spent</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Device identifiers and technical information</span>
                </li>
              </ul>
            </motion.div>

            {/* Section 5 - Use of Personal Data */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Users className="h-8 w-8 text-indigo-600" />
                <h2 className="text-2xl font-bold text-gray-900">5. Use of Your Personal Data</h2>
              </div>
              <p className="text-gray-700 mb-4">
                We may share your personal information in the following situations:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2"></div>
                  <div>
                    <strong>With Service Providers:</strong> To monitor and analyze use, support and maintain our Service, 
                    advertise, process payments, and contact you.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2"></div>
                  <div>
                    <strong>For business transfers:</strong> In connection with mergers, sales of assets, financing, 
                    or acquisitions.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2"></div>
                  <div>
                    <strong>With Affiliates:</strong> We may share information with parent companies, subsidiaries, 
                    and joint venture partners.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-indigo-600 rounded-full mt-2"></div>
                  <div>
                    <strong>With business partners:</strong> To offer certain products, services, or promotions.
                  </div>
                </li>
              </ul>
            </motion.div>

            {/* Section 9 - Sales of Personal Information */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <CreditCard className="h-8 w-8 text-yellow-600" />
                <h2 className="text-2xl font-bold text-gray-900">9. Sales of Personal Information</h2>
              </div>
              <p className="text-gray-700 mb-4">
                The Company may monetize data in the following ways:
              </p>
              <div className="space-y-4">
                <div className="border-l-4 border-yellow-600 pl-4">
                  <h3 className="font-semibold mb-2">Direct Data Monetization</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Selling data to businesses, government agencies, universities, and research facilities</li>
                    <li>• Developing APIs for customer data access</li>
                    <li>• Licensing data to educational institutions</li>
                    <li>• Selling data analyses to housing authorities and nonprofits</li>
                    <li>• Trading data for benchmarking or services</li>
                  </ul>
                </div>
                <div className="border-l-4 border-yellow-600 pl-4">
                  <h3 className="font-semibold mb-2">Indirect Data Monetization</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Developing new services and products</li>
                    <li>• Identifying market gaps</li>
                    <li>• Improving operational efficiency</li>
                    <li>• Enhancing customer service</li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Section 10 - Security */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.0 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Lock className="h-8 w-8 text-green-600" />
                <h2 className="text-2xl font-bold text-gray-900">10. How We Protect Your Personal Information</h2>
              </div>
              <p className="text-gray-700 mb-4">
                We take appropriate security measures including:
              </p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">SSL encryption for data transmission</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">256-bit AES encryption for stored data</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Professional colocation centers with physical security</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">Protection against session hijacks and SQL injection</span>
                </li>
              </ul>
              <div className="mt-4 p-4 bg-yellow-50 rounded-xl">
                <p className="text-gray-700">
                  <strong>Note:</strong> No system can be completely secure. While we take steps to secure your information, 
                  we cannot guarantee absolute security.
                </p>
              </div>
            </motion.div>

            {/* Section 13 - Children's Privacy */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.1 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <UserX className="h-8 w-8 text-red-600" />
                <h2 className="text-2xl font-bold text-gray-900">13. Children's Privacy</h2>
              </div>
              <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded-r-xl">
                <p className="text-gray-800 mb-3">
                  <strong>This Service is not intended for children under 13 years of age.</strong> We do not knowingly collect 
                  personal information from children under 13. If you are under 13, you must not use this Service without 
                  verified parental consent.
                </p>
                <p className="text-gray-700">
                  In compliance with COPPA, we require verified parental consent before knowingly collecting any personal 
                  information from children under 13. Parents who believe their child has provided information should contact us at{' '}
                  <a href="mailto:privacy@gethousingai.com" className="text-blue-600 hover:underline">privacy@gethousingai.com</a>.
                </p>
              </div>
            </motion.div>

            {/* Section 14 - Geographic Limitations */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="bg-white rounded-3xl p-8 shadow-lg mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Globe className="h-8 w-8 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900">14. Geographic Limitations</h2>
              </div>
              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-r-xl mb-4">
                <p className="text-gray-800">
                  <strong>This Service is intended solely for users located within the Commonwealth of Massachusetts, USA.</strong>
                </p>
              </div>
              <p className="text-gray-700 mb-3">
                By accessing the Service from outside Massachusetts, you acknowledge that:
              </p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">You do so at your own risk and responsibility</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">The Service may not comply with laws outside Massachusetts</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">We are not responsible for violations of your local laws</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                  <span className="text-gray-700">We may limit or terminate access from outside Massachusetts</span>
                </li>
              </ul>
            </motion.div>

            {/* Section 15 - Changes */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.3 }}
              className="bg-white rounded-3xl p-8 shadow-lg"
            >
              <div className="flex items-center gap-3 mb-6">
                <Calendar className="h-8 w-8 text-purple-600" />
                <h2 className="text-2xl font-bold text-gray-900">15. Changes to This Notice</h2>
              </div>
              <p className="text-gray-700">
                We may update this Privacy Notice from time to time. When we make changes, we will post the revised notice 
                here and update the "Last updated" date at the top. Your continued use of the chat widget after any changes 
                are posted will signify your acceptance of the updated terms.
              </p>
              <p className="mt-4 text-gray-600 italic">Last updated: September 05, 2025</p>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default PrivacyPage;